import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as S from './styles';
import {
  Flex, Text, Box, Link,
} from './components';
import PhoneNumberInput from './PhoneNumberInput';
import UsPhoneNumberInput from './UsPhoneNumberInput';
import OptionalShortText from './OptionalShortText';
import identityConfig from './collect-info/identityConfig';
import { uniq } from '../../assets/js/utils';

export const formatEinValue = (value) => {
  const formattedValue = value.replace(/[^0-9]/g, '');
  if (formattedValue.length <= 2) {
    return formattedValue;
  }
  if (formattedValue.length <= 9) {
    return `${formattedValue.slice(0, 2)}-${formattedValue.slice(2)}`;
  }
  return formattedValue;
};


export function Question({
  title,
  type,
  isRequired,
  errorMessage,
  deletable,
  onDeleteRow,
  reviewActions,
  onCheckEdit,
  onCheckRedo,
  isEditChecked,
  isRedoChecked,
  reviewerNote,
  additionalNote,
  dateFormat,
  categoryType,
  isRequiredCategoryValueExisted,
  requiredCategoryTitle,
  noDataError,
  hideInput,
  ...props
}) {
  let formControl;
  let formElement;
  if (type === 'adp_country_code') return null;
  if (type === 'adp_area_code') return null;

  const isOnboardingV2 = window.isOnboardingV2 === 'true';

  const onChangeFnMultSelect = (codeValue, checked) => {
    let value = [...props.value];
    if (checked) {
      value = uniq([...value, codeValue]);
    } else {
      value = value.filter((v) => v !== codeValue);
    }
    props.onChange({ name: props.name, value });
  };

  const reformatSSN = (ssn) => {
    if (!ssn) return ssn;

    return ssn
      .replace(/[^\d-*]/g, '')
      .replace(/^([\d|\*]{3})/, '$1-')
      .replace(/-([\d|\*]{2})/, '-$1-')
      .replace(/([\d|\*])-([\d|\*]{4}).*/, '$1-$2');
  };

  const [isEditing, setIsEditing] = useState(false);

  switch (type) {
    case 'adp_phone_number':
      formControl = (
        <PhoneNumberInput
          {...props}
          onChange={(value) => {
            props.onChange({ name: props.name, value });
          }}
        />
      );
      break;
    case 'date':
      formControl = (
        <S.Input
          {...props}
          type="text"
          data-testid="basic-info-dob-input"
          placeholder="MM/DD/YYYY"
          onChange={(e) => {
            props.onChange({ name: props.name, value: e.target.value });
          }}
        />
      );
      break;
    case 'dropdown':
      let { options, optionLabelDescriptionFirst, disabled } = props;
      if (options && options.codeListTitle) {
        options = options.listItems;
      }
      let filterOptions = [];
      if (isRequiredCategoryValueExisted && options && options.length > 0 && options[0].category) {
        filterOptions = options.filter((op) => op.category === props.requiredCategoryValue);
      }
      if (filterOptions.length !== 0) {
        options = filterOptions;
      }
      formControl = (
        <S.Select
          {...props}
          disabled={disabled}
          onChange={(e) => {
            props.onChange({ name: props.name, value: e.target.value });
          }}
        >
          <option disabled value="" style={{ display: 'none', color: '#666' }} key={0}>
            Select from dropdown
          </option>
          {options
            && options.map((option, idx) => (
              <option value={option.codeValue || option.code} key={idx}>
                {optionLabelDescriptionFirst
                  ? option.description
                      || option.name
                      || option.shortName
                      || option.longName
                      || option.valueDescription
                      || option.code
                  : option.shortName
                      || option.longName
                      || option.valueDescription
                      || option.name
                      || option.description
                      || option.code}
              </option>
            ))}
        </S.Select>
      );
      break;
    case 'searchable_dropdown':
      let { options: searchableOptions, disabled: searchableDisabled } = props;
      options = [];
      let searchableValue = null;
      if (searchableOptions) {
        let filteredOptions = [];
        if (isRequiredCategoryValueExisted && searchableOptions && searchableOptions.length > 0) {
          filteredOptions = searchableOptions.filter((op) => op.foreignKey === props.requiredCategoryValue || op.foreignKey === null);
        }
        if (filteredOptions.length > 0) {
          searchableOptions = filteredOptions;
        }

        searchableOptions.forEach((option) => {
          options.push({
            value: option.codeValue,
            label: option.shortName,
          });
          if (props.value === option.codeValue) {
            searchableValue = options[options.length - 1];
          }
        });
      }
      formControl = (
        <S.CustomSelect
          classNamePrefix="Select"
          {...props}
          value={searchableValue}
          disabled={searchableDisabled}
          options={options}
          isSearchable
          onChange={(e) => {
            props.onChange({ name: props.name, value: e.value });
          }}
        />
      );
      break;
    case 'short_text':
      formControl = (
        <S.Input
          {...props}
          type="text"
          onChange={(e) => {
            props.onChange({ name: props.name, value: e.target.value });
          }}
        />
      );
      break;
    case 'optional_short_text':
      formControl = <OptionalShortText props={props} />;
      break;
    case 'long_text':
      formControl = (
        <S.TextArea
          {...props}
          rows={5}
          onChange={(e) => {
            props.onChange({ name: props.name, value: e.target.value });
          }}
        />
      );
      break;
    case 'attachment':
      const { value } = props;
      delete props.value;
      const fileLink = value.pdf_url || value.url;
      const fileName = value.pdf_file_name || value.file_name || 'uploaded file';
      if (fileLink && props.disabled) {
        formControl = (
          <a href={fileLink} target="_blank" rel="noreferrer">
            {fileName}
          </a>
        );
      } else {
        formControl = (
          <>
            <input
              {...props}
              type="file"
              style={{
                ...(props.disabled && { opacity: 0.5 }),
              }}
              onChange={(e) => {
                props.onChange({
                  name: props.name,
                  value: {
                    [props.name]: e.target.value,
                    attachment: e.target.files[0],
                  },
                });
              }}
            />
            { fileLink && isEditChecked
              && <Text fontSize="11px" marginTop="7px">Editing without choosing a file replacement will delete the employee's uploaded file and you will no longer have access to it.</Text>}
          </>
        );
      }
      break;
    case 'multiple_selection':
      formControl = (
        <Flex column>
          {props.options.map((option) => {
            const checked = props.value.indexOf(option.codeValue) !== -1;
            return (
              <Flex>
                <label>
                  <Flex alignItems="center">
                    <S.Input
                      {...props}
                      type="checkbox"
                      value={option.codeValue}
                      onChange={(e) => {
                        onChangeFnMultSelect(option.codeValue, e.target.checked);
                      }}
                      checked={checked}
                    />
                    <Box ml={isOnboardingV2 ? 2 : 1} />
                    {option.shortName}
                  </Flex>
                </label>
              </Flex>
            );
          })}
        </Flex>
      );
      break;
    case 'long_multiple_selection':
      formControl = (
        <Flex column>
          {props.options.map((option) => {
            const checked = props.value.indexOf(option.codeValue) !== -1;
            return (
              <Flex style={{ marginBottom: '15px' }}>
                <label>
                  <Flex>
                    <S.Input
                      style={{ height: '22px' }}
                      {...props}
                      type="checkbox"
                      value={option.codeValue}
                      onChange={(e) => {
                        onChangeFnMultSelect(option.codeValue, e.target.checked);
                      }}
                      checked={checked}
                    />
                    <Box ml={1} />
                    {option.shortName}
                  </Flex>
                </label>
              </Flex>
            );
          })}
        </Flex>
      );
      break;
    case 'multiple_choice':
      formControl = isOnboardingV2 ? (
        <Flex column>
          {props.options.map((option) => (
            <Flex>
              <label>
                <Flex alignItems="center">
                  <S.Input
                    {...props}
                    type="radio"
                    checked={props.value === option.codeValue}
                    onChange={(e) => {
                      if (e.target.checked) {
                        props.onChange({
                          name: props.name,
                          value: option.codeValue,
                        });
                      }
                    }}
                  />
                  <Box ml={2} />
                  {option.shortName}
                </Flex>
              </label>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Flex column>
          {props.options.map((option) => (
            <Flex>
              <label>
                <Flex alignItems="start">
                  <S.Input
                    style={{ marginBottom: '5px' }}
                    {...props}
                    type="radio"
                    checked={props.value === option.codeValue}
                    onChange={(e) => {
                      if (e.target.checked) {
                        props.onChange({
                          name: props.name,
                          value: option.codeValue,
                        });
                      }
                    }}
                  />
                  <Box ml={1} />
                  {option.shortName}
                </Flex>
              </label>
            </Flex>
          ))}
        </Flex>
      );
      break;
    case 'long_multiple_choice':
      formControl = (
        <Flex column>
          {props.options.map((option) => (
            <Flex style={{ marginBottom: '15px' }}>
              <label>
                <Flex>
                  <S.Input
                    style={{ height: '22px' }}
                    {...props}
                    type="radio"
                    checked={props.value === option.codeValue}
                    onChange={(e) => {
                      if (e.target.checked) {
                        props.onChange({
                          name: props.name,
                          value: option.codeValue,
                        });
                      }
                    }}
                  />
                  <Box ml={1} />
                  {option.shortName}
                </Flex>
              </label>
            </Flex>
          ))}
        </Flex>
      );
      break;
    case 'telephone':
      formControl = (
        <PhoneNumberInput
          {...props}
          onChange={(value) => {
            props.onChange({ name: props.name, value });
          }}
        />
      );
      break;
    case 'us_telephone':
      formControl = (
        <UsPhoneNumberInput
          {...props}
          onChange={(value) => {
            props.onChange({ name: props.name, value });
          }}
        />
      );
      break;
    case 'email':
      formControl = (
        <S.Input
          {...props}
          type="text"
          onChange={(e) => {
            props.onChange({ name: props.name, value: e.target.value });
          }}
        />
      );
      break;
    case 'identity':
      const { identityType, countryCode } = props;
      switch (identityType) {
        case 'dl':
          const { value = {} } = props;
          const { state_code: stateCode, [props.name]: licenseValue } = value;
          const states = getStates(countryCode, identityType, identityConfig);
          formControl = (
            <Flex>
              <S.Select
                {...props}
                name={`${props.name}[state_code]`}
                value={stateCode}
                onChange={(e) => {
                  props.onChange({ name: `${props.name}[state_code]`, value: e.target.value });
                }}
              >
                <option disabled selected value={undefined} style={{ display: 'none' }}>
                  Select state
                </option>
                {states.map((s) => <option value={s.state_code}>{s.state_name}</option>)}
              </S.Select>
              <Box mr={10} />
              <S.Input
                type="text"
                {...props}
                value={licenseValue}
                onChange={(e) => {
                  props.onChange({ name: props.name, value: e.target.value });
                }}
              />
            </Flex>
          );
          break;
        case 'ssn':
          formControl = (
            <S.QuestionWrapper>
              {isEditing
                ? (
                  <S.Input
                    type="text"
                    data-testid="basic-info-ssn-input"
                    {...props}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').substr(0, 9);
                      props.onChange({ name: props.name, value: val });
                    }}
                    onBlur={() => { setIsEditing(!isEditing); }}
                  />
                ) : (
                  <S.Input
                    type="text"
                    data-testid="basic-info-ssn-input"
                    value={reformatSSN(props.value)}
                    onFocus={() => {
                      setIsEditing(!isEditing);
                    }}
                  />
                )}
            </S.QuestionWrapper>
          );
          break;
        case 'nric':
          formControl = (
            <S.Input
              type="text"
              {...props}
              onChange={(e) => {
                props.onChange({ name: props.name, value: e.target.value });
              }}
            />
          );
      }
      break;
    case 'text_heading':
      formElement = (
        <Flex column className="form-text-heading" mb={20} dangerouslySetInnerHTML={{ __html: props.content }} />
      );
      break;
    case 'image':
      const { attachment } = props;
      formElement = (
        <Flex mb={20} maxWidth="100%">
          <img style={{ maxWidth: '100%' }} src={attachment.url} />
        </Flex>
      );
      break;
    case 'iframe':
      const { iframeURL } = props;
      const isYoutubeIframe = iframeURL.indexOf('youtube.com') !== -1;
      formElement = (
        <Flex mb={20} style={{ maxWidth: '100%' }} column>
          <div className="form-embed-wrapper">
            <div className={isYoutubeIframe && 'form-youtube-iframe'} dangerouslySetInnerHTML={{ __html: iframeURL }} />
          </div>
        </Flex>
      );
      break;
    case 'embed_youtube':
      const { content } = props;
      const youtubeURL = `https://www.youtube.com/embed/${getYoutubeID(content)}`;
      formElement = (
        <Flex mb={20} style={{ maxWidth: '100%' }} column>
          <div className="form-embed-wrapper">
            <div className="form-youtube-iframe">
              <iframe
                width="560"
                height="315"
                src={youtubeURL}
                frameBorder={0}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </Flex>
      );
      break;
    case 'link':
      formElement = (
        <Flex mb={20} style={{ maxWidth: '100%' }} column>
          <Link href={props.content} target="_blank">
            {title}
          </Link>
        </Flex>
      );
      break;
    case 'alien_registration_number':
      formControl = (
        <S.Input
          {...props}
          type="text"
          onChange={(e) => {
            if (e.target.value.startsWith('A')) {
              props.onChange({ name: props.name, value: e.target.value });
            } else {
              props.onChange({ name: props.name, value: 'A' + e.target.value });
            }
          }}
        />
      );
      break;
    case 'ssn':
      const { value: ssnValue, ...rest } = props;
      formControl = (
        <S.QuestionWrapper>
          <S.Input
            type="text"
            {...rest}
            value={reformatSSN(ssnValue)}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              props.onChange({ name: props.name, value: val });
            }}
          />
        </S.QuestionWrapper>
      );
      break;
    default:
      formControl = (
        <div>
          Cannot render question
          {' '}
          {title}
          (
          {type}
          )
        </div>
      );
  }

  if (formElement) {
    return formElement;
  }

  const reviewActionButtons = (() => {
    if (reviewActions.length === 0) return null;
    return (
      <Flex ml={2}>
        {reviewActions.indexOf('edit') !== -1 && (
          <Flex alignItems="center">
            <input
              // disabled={hrSubmission.status === 'not_started'}
              type="checkbox"
              checked={isEditChecked}
              onChange={(e) => {
                onCheckEdit(e.target.checked);
              }}
            />
            <Box mr={1} />
            <Text fontSize="12px" semibold>
              Edit
            </Text>
          </Flex>
        )}
        {reviewActions.indexOf('redo') !== -1 && (
          <Flex alignItems="center" ml={12}>
            <input
              type="checkbox"
              checked={isRedoChecked}
              onChange={(e) => {
                onCheckRedo(e.target.checked);
              }}
            />
            <Box mr={1} />
            <Text fontSize="12px" semibold>
              Re-submit
            </Text>
          </Flex>
        )}
      </Flex>
    );
  })();

  return (
    <S.QuestionWrapper mb="20px" hasError={errorMessage}>
      <Flex>
        <Flex justifyContent="space-between" alignItems="center" width={1} mb="7px">
          <S.QuestionLabel isRequired={isRequired} disabled={props.disabled}>
            {title}
          </S.QuestionLabel>
          <Flex flexShrink={0}>{reviewActionButtons}</Flex>
        </Flex>
      </Flex>
      <Flex position="relative" flexDirection="column">
        {noDataError && isRequired && <S.QuestionErrorMessage>{`${noDataError}`}</S.QuestionErrorMessage>}
        {noDataError && !isRequired && <Text>{`${noDataError}`}</Text>}
        {!noDataError && categoryType && !isRequiredCategoryValueExisted && isRequired && (
          <S.QuestionErrorMessage>{`Please select ${requiredCategoryTitle} first`}</S.QuestionErrorMessage>
        )}
        {!noDataError && categoryType && !isRequiredCategoryValueExisted && !isRequired && (
          // <Text color="gray" mt="-5px" fontSize="12px">{`Select ${requiredCategoryTitle} first`}</Text>
          <S.OptionalQuestionWarningMessage>{`Select ${requiredCategoryTitle} first`}</S.OptionalQuestionWarningMessage>
        )}
        {!noDataError && !hideInput
          && (!categoryType || (categoryType && isRequiredCategoryValueExisted)) && formControl}
        {deletable && (
          <Box mt="8px" position="absolute" right="-22px">
            <S.DeleteButton
              onClick={(e) => {
                e.preventDefault();
                onDeleteRow();
              }}
            />
          </Box>
        )}
      </Flex>
      {additionalNote && <Text fontSize="12px">{additionalNote}</Text>}
      {errorMessage && <S.QuestionErrorMessage>{errorMessage}</S.QuestionErrorMessage>}
    </S.QuestionWrapper>
  );
}

Question.propTypes = {
  // key: PropTypes.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  isRequired: PropTypes.bool.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  // question type specific data
  options: PropTypes.array,
  dateFormat: PropTypes.string,
};

Question.defaultProps = {
  reviewActions: [],
};

export function QuestionGroup({ children, extendable, onExtend }) {
  if (children.props && children.props.children) {
    children = children.props.children;
  }
  return (
    <div>
      {children}
      {extendable && (
        <Flex onClick={onExtend} alignItems="center" mt="-12px" mb={20} style={{ cursor: 'pointer' }}>
          <img src="/j/images/employee-onboarding/add-question.svg" alt="add button" width={20} />
          <Text color="#0D8CE6" ml="8px" fontSize="12px">
            Add another answer
          </Text>
        </Flex>
      )}
    </div>
  );
}

export function RedoNoteInput({ onChange, value }) {
  return (
    <Flex column mb={34}>
      <Text fontSize="12px" mb="7px">
        Add a note to employee to explain why it needs be re-submitted
      </Text>
      <S.TextArea
        rows={6}
        onChange={onChange}
        value={value}
        placeholder="Examples:&#10;Your uploaded image is too blurry.&#10;Your licence is expired.&#10;This ID is invalid."
      />
    </Flex>
  );
}

export function BadAnswerNote({ note }) {
  return (
    <Flex column mb={34} bg="#c33e3e" borderRadius="5px" p={1}>
      <Text fontSize="12px" mb="7px" color="white">
        {note}
      </Text>
    </Flex>
  );
}

// help functions
const getStates = (countryCode, identityType, identityConfig) => {
  const countryInfo = identityConfig.identity_regexes.filter((countryInfo) => countryInfo.country_code === countryCode)[0];
  if (!countryInfo) return [];

  const identityInfo = countryInfo.identity_types.filter((identityInfo) => identityInfo.value === identityType)[0];
  if (!identityInfo) return [];
  return identityInfo.states;
};

const getYoutubeID = (url) => {
  url = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
  return url[2] !== undefined ? url[2].split(/[^0-9a-z_\-]/i)[0] : url[0];
};
